/**
 * Order actions ported from reware-admin's Orders portal (orders-api.js /
 * app-api.js). Each hits the same admin_graphql mutation the admin portal uses.
 */
import { execAdmin } from '@src/apis/reware/utils/exec';

import type { DeliveryAddress } from './types';

interface OrderMutationResponse {
  order: Record<string, { id: number } | null> | null;
}

/**
 * Runs an order mutation and verifies the root actually produced a result.
 *
 * The check is not optional. `execAdmin` throws only when the payload carries `errors[0].message`,
 * and `order(id:)` resolves `null` for an id that does not exist — no error at all. Awaiting and
 * discarding reported a green "Payment captured" / "Order refund applied" for operations that
 * never happened, on four actions that move money.
 *
 * Note what this does NOT catch. The mutation root is gated by `canEdit('orders')` with no
 * partner binding (reware-api lib/graphql/admin/mutations/order/index.js), and `resolver(Order)`
 * looks the order up by primary key — so an id belonging to another partner resolves normally and
 * the mutation runs. This guard covers the not-found case only; it is not an authorization check.
 */
const runOrderMutation = async <V>(
  orderId: number,
  field: string,
  query: string,
  variables: V
): Promise<void> => {
  const data = await execAdmin<V, OrderMutationResponse>(query, { variables });

  if (!data?.order?.[field]) {
    throw new Error(`Order ${orderId} did not accept the ${field} request — nothing was changed`);
  }
};

/**
 * Dollar amounts come from free-text dialog inputs. `parseFloat` happily yields a negative, and
 * the server has no reason to reject one, so a mistyped "-50" would issue a negative refund.
 */
const requirePositiveAmount = (amount: number, label: string): void => {
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error(`${label} must be greater than zero`);
  }
};

export const cancelOrder = async (orderId: number): Promise<void> => {
  await runOrderMutation(
    orderId,
    'cancel',
    'mutation cancel($orderId: Int!) { order(id: $orderId) { cancel { id } } }',
    { orderId }
  );
};

export const capturePayment = async (orderId: number): Promise<void> => {
  await runOrderMutation(
    orderId,
    'capturePayment',
    'mutation capturePayment($orderId: Int!) { order(id: $orderId) { capturePayment { id orderCapturedAt } } }',
    { orderId }
  );
};

export const shortShipOrder = async (orderId: number): Promise<void> => {
  await runOrderMutation(
    orderId,
    'shortShip',
    'mutation shortShip($orderId: Int!) { order(id: $orderId) { shortShip { id } } }',
    { orderId }
  );
};

export const extendReturnability = async (orderId: number): Promise<void> => {
  await runOrderMutation(
    orderId,
    'extendReturnability',
    'mutation extendReturnability($orderId: Int!) { order(id: $orderId) { extendReturnability { id returnableUntil } } }',
    { orderId }
  );
};

/**
 * One item to return. Mirrors `OrderReturnItemAdmin`, whose four fields are all required except
 * `comment`.
 *
 * `uuid` is the INVENTORY ITEM's uuid, not the order item's: `initiateReturns` loads order items
 * by `include: { model: InventoryItem, where: { uuid: returnItems.map(i => i.uuid) } }` and then
 * matches each back with `returnItems.find(i => i.uuid === orderItem.InventoryItem.uuid)`
 * (reware-api lib/services/returns.js). Passing the order item's own uuid silently matches
 * nothing.
 */
export interface ReturnItemInput {
  id: number;
  inventoryItemUuid: string;
}

/**
 * Initiates a return for the given items with free return shipping. The admin portal's full flow
 * lets an operator pick items and a refund split; this dashboard version returns every returnable
 * item on the order under one reason.
 *
 * `reason` comes from the problem-order list (`constants.return.problemReturnReasons`) rather than
 * the customer list, because free return shipping is the problem-order flow.
 */
export const initiateReturn = async (
  orderId: number,
  items: ReturnItemInput[],
  reason: string,
  comment?: string
): Promise<void> => {
  // Without this the request is accepted and does nothing: with no returnable items the service
  // still resolves the order, so runOrderMutation sees a result, the operator is told "Return
  // initiated", and a return email goes to the customer for an empty return.
  if (!items.length) {
    throw new Error('No returnable items on this order');
  }
  if (!reason) {
    throw new Error('A return reason is required');
  }

  await runOrderMutation(
    orderId,
    'initiateReturns',
    `mutation initiateReturns($orderId: Int!, $returnItems: [OrderReturnItemAdmin]!, $isFreeReturnShipping: Boolean!, $overrideEmail: String!) {
      order(id: $orderId) {
        initiateReturns(returnItems: $returnItems, isFreeReturnShipping: $isFreeReturnShipping, overrideEmail: $overrideEmail) { id }
      }
    }`,
    {
      orderId,
      // id and uuid are String! on the input type even though the order item id is numeric.
      returnItems: items.map(item => ({
        id: String(item.id),
        uuid: item.inventoryItemUuid,
        reason,
        comment,
      })),
      isFreeReturnShipping: true,
      overrideEmail: '',
    }
  );
};

export const createWebhookEvent = async (orderId: number): Promise<void> => {
  await runOrderMutation(
    orderId,
    'createWebhookEvent',
    'mutation createWebhookEvent($orderId: Int!) { order(id: $orderId) { createWebhookEvent { id } } }',
    { orderId }
  );
};

export const sendUpdatedConfirmation = async (orderId: number): Promise<void> => {
  await runOrderMutation(
    orderId,
    'sendUpdatedConfirmation',
    'mutation sendUpdatedConfirmation($orderId: Int!) { order(id: $orderId) { sendUpdatedConfirmation { id } } }',
    { orderId }
  );
};

export const applyFreeShipping = async (orderId: number, reason: string, note?: string): Promise<void> => {
  await runOrderMutation(
    orderId,
    'applyFreeShipping',
    `mutation applyFreeShipping($orderId: Int!, $reason: String!, $note: String) {
      order(id: $orderId) { applyFreeShipping(reason: $reason, note: $note) { id } }
    }`,
    { orderId, reason, note }
  );
};

export interface IssueGiftcardInput {
  orderId: number;
  amount: number; // dollars
  email: string;
  reason: string;
  note?: string;
}

export const issueGiftcard = async ({ orderId, amount, email, reason, note }: IssueGiftcardInput): Promise<void> => {
  requirePositiveAmount(amount, 'Gift card amount');
  const cents = Math.round(amount * 100);
  await runOrderMutation(
    orderId,
    'issueGiftcard',
    `mutation issueGiftcard($orderId: Int!, $reason: String!, $note: String, $amount: Int!, $email: String!) {
      order(id: $orderId) { issueGiftcard(reason: $reason, note: $note, amount: $amount, email: $email) { id } }
    }`,
    { orderId, reason, note, amount: cents, email }
  );
};

export interface ApplyOrderRefundInput {
  orderId: number;
  orderLevelRefund: number; // dollars
  refundReason: string;
  comments?: string;
  isRefundingSalesTax?: boolean;
  applyOrderFreeShipping?: boolean;
}

export const applyOrderRefund = async (input: ApplyOrderRefundInput): Promise<void> => {
  requirePositiveAmount(input.orderLevelRefund, 'Refund amount');
  const cents = Math.round(input.orderLevelRefund * 100);
  await runOrderMutation(
    input.orderId,
    'applyOrderRefund',
    `mutation applyOrderRefund(
      $orderId: Int!, $comments: String, $refundReason: String,
      $orderLevelRefund: Int, $applyOrderFreeShipping: Boolean, $isRefundingSalesTax: Boolean,
      $conditionGradeRefunds: [ConditionGradeRefundInputTypeAdmin],
      $itemLevelRefunds: [ItemLevelRefundInputTypeAdmin],
      $returnYpackageRefunds: [ReturnYpackageRefundInputTypeAdmin]
    ) {
      order(id: $orderId) {
        applyOrderRefund(
          comments: $comments, refundReason: $refundReason, orderLevelRefund: $orderLevelRefund,
          applyOrderFreeShipping: $applyOrderFreeShipping, isRefundingSalesTax: $isRefundingSalesTax,
          conditionGradeRefunds: $conditionGradeRefunds, itemLevelRefunds: $itemLevelRefunds,
          returnYpackageRefunds: $returnYpackageRefunds
        ) { id }
      }
    }`,
    {
      orderId: input.orderId,
      comments: input.comments,
      refundReason: input.refundReason,
      orderLevelRefund: cents,
      applyOrderFreeShipping: input.applyOrderFreeShipping ?? false,
      isRefundingSalesTax: input.isRefundingSalesTax ?? false,
      // Required in practice though the schema marks them optional: OrderService.applyOrderRefund
      // destructures all three without defaults and immediately reduces them, so omitting any one
      // fails with "Cannot read properties of undefined (reading 'reduce')" before the refund is
      // even considered. reware-admin always sends all three.
      conditionGradeRefunds: [],
      itemLevelRefunds: [],
      returnYpackageRefunds: [],
    }
  );
};

export type UpdateShippingAddressInput = { orderId: number } & Partial<
  Pick<DeliveryAddress, 'address1' | 'address2' | 'city' | 'state' | 'postalCode'>
>;

export const updateShippingAddress = async (input: UpdateShippingAddressInput): Promise<void> => {
  const { orderId, ...address } = input;
  await runOrderMutation(
    orderId,
    'update',
    `mutation updateOrder($id: Int!, $address1: String, $address2: String, $city: String, $state: String, $postalCode: String) {
      order(id: $id) { update(address1: $address1, address2: $address2, city: $city, state: $state, postalCode: $postalCode) { id } }
    }`,
    { id: orderId, ...address }
  );
};

/**
 * Not wired to any affordance yet — the Emails activity tab is read-only.
 *
 * Deliberately NOT result-checked, unlike every mutation above. `EmailAdmin` declares
 * `success: Boolean`, but the resolver returns `TxService.resendEmails(...)`, which resolves the
 * bare boolean `true` — and graphql-js's default resolver cannot read a field off a primitive, so
 * `success` always comes back `null` even on a complete success. Asserting on it reports failure
 * for every resend that worked. The service throws on failure, and `execAdmin` surfaces that, so
 * "did it reject" is the only signal available here.
 */
export const resendEmails = async (orderId: number, emailIds: number[]): Promise<void> => {
  await execAdmin<{ orderId: number, emailIds: number[] }, unknown>(
    `mutation resendEmails($orderId: Int, $emailIds: [Int]!) {
      email { resendEmails(orderId: $orderId, emailIds: $emailIds) { success } }
    }`,
    { variables: { orderId, emailIds } }
  );
};
